import { LitElement, html, css } from 'lit';

/**
 * CrusherPlanCard
 *
 * SaaS pricing-tier card. Renders a plan name + price + tagline header,
 * a slotted feature list in the body, and a slotted CTA at the bottom.
 * Supports two emphasis variants:
 *
 *   - featured  — visually elevated tier (the "recommended" one); brand-color border + slight shadow lift
 *   - current   — "this is the user's active plan"; replaces the CTA emphasis with a checkmark + muted state
 *
 * Both can coexist: a `featured` + `current` card shows the brand outline + the current-plan badge.
 *
 * Attributes:
 *   - plan-name   (string) — required; the tier label (e.g., "Pro")
 *   - price       (string) — required; the price tag (e.g., "$99/mo", "$1,200/yr", "Free")
 *   - description (string) — optional; one-line tagline under the price
 *   - featured    (boolean) — emphasize as the recommended tier
 *   - current     (boolean) — mark as the user's active plan
 *
 * Slots:
 *   - default — feature list. Consumer slots a <ul> of <li> items (or any structure).
 *               Each top-level child gets baseline list-item styling; the consumer can
 *               prefix items with check icons via their own markup.
 *   - cta     — the action button (Upgrade / Select / Manage / Current plan).
 *   - badge   — optional badge above the plan name (e.g., "Most popular", "Save 20%").
 *
 * Tokens used: --crusher-surface, --crusher-border, --crusher-color-brand-primary,
 *   --crusher-fg, --crusher-fg-muted, --crusher-feedback-success, --crusher-shadow-{md,lg},
 *   plus the standard spacing / radius scale.
 *
 * A11y: the host is a single landmark with role="group" + aria-label="<plan-name> plan".
 *   The "current plan" indicator is announced via the badge text.
 */
export class CrusherPlanCard extends LitElement {
  static properties = {
    planName:    { type: String, attribute: 'plan-name', reflect: true },
    price:       { type: String, reflect: true },
    description: { type: String, reflect: true },
    featured:    { type: Boolean, reflect: true },
    current:     { type: Boolean, reflect: true }
  };

  constructor() {
    super();
    this.planName = '';
    this.price = '';
    this.description = '';
    this.featured = false;
    this.current = false;
  }

  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      background: var(--crusher-surface);
      color: var(--crusher-fg);
      border: var(--crusher-component-border-weight) solid var(--crusher-border);
      border-radius: var(--crusher-radius-lg);
      padding: var(--crusher-spacing-6);
      gap: var(--crusher-spacing-4);
      min-width: 16rem;
      max-width: 22rem;
      transition: border-color var(--crusher-motion-duration-base) var(--crusher-motion-easing-standard),
                  box-shadow var(--crusher-motion-duration-base) var(--crusher-motion-easing-standard),
                  transform var(--crusher-motion-duration-base) var(--crusher-motion-easing-standard);
    }
    :host([featured]) {
      border-color: color-mix(in srgb, var(--crusher-color-brand-primary) 60%, var(--crusher-border));
      box-shadow: var(--crusher-shadow-lg, var(--crusher-shadow-2));
      /* Tiny visual lift to draw the eye without becoming distracting. */
      transform: translateY(-2px);
    }
    @media (prefers-reduced-motion: reduce) {
      :host([featured]) { transform: none; }
    }
    :host([current]) {
      background: color-mix(in srgb, var(--crusher-color-brand-primary) 4%, var(--crusher-surface));
    }

    .header {
      display: flex;
      flex-direction: column;
      gap: var(--crusher-spacing-1);
    }

    .badges {
      display: flex;
      flex-wrap: wrap;
      gap: var(--crusher-spacing-2);
      min-height: 1.25rem; /* keep header alignment steady when no badge */
    }
    .badge-builtin {
      display: inline-flex;
      align-items: center;
      gap: var(--crusher-spacing-1);
      padding: var(--crusher-spacing-1) var(--crusher-spacing-2);
      border-radius: var(--crusher-radius-full);
      font-size: var(--crusher-font-size-xs);
      font-weight: var(--crusher-font-weight-semibold);
      background: color-mix(in srgb, var(--crusher-feedback-success) 14%, transparent);
      color: var(--crusher-feedback-success);
    }
    .badge-builtin .dot {
      width: 0.4rem;
      height: 0.4rem;
      border-radius: var(--crusher-radius-full);
      background: currentColor;
    }
    .badges ::slotted([slot="badge"]) {
      display: inline-flex;
      align-items: center;
      padding: var(--crusher-spacing-1) var(--crusher-spacing-2);
      border-radius: var(--crusher-radius-full);
      font-size: var(--crusher-font-size-xs);
      font-weight: var(--crusher-font-weight-semibold);
      background: color-mix(in srgb, var(--crusher-color-brand-primary) 14%, transparent);
      color: var(--crusher-color-brand-primary);
    }

    .plan-name {
      font-size: var(--crusher-font-size-xl);
      font-weight: var(--crusher-font-weight-semibold);
      margin: 0;
      line-height: var(--crusher-line-height-tight);
    }

    .price {
      display: flex;
      align-items: baseline;
      gap: var(--crusher-spacing-1);
      font-size: var(--crusher-font-size-3xl);
      font-weight: var(--crusher-font-weight-bold);
      line-height: var(--crusher-line-height-tight);
      color: var(--crusher-fg);
    }

    .description {
      color: var(--crusher-fg-muted);
      font-size: var(--crusher-font-size-sm);
      line-height: var(--crusher-line-height-snug);
      margin: 0;
    }

    .features {
      flex: 1; /* push CTA to the bottom regardless of feature list length */
      display: block;
    }
    .features ::slotted(ul) {
      list-style: none;
      padding: 0;
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: var(--crusher-spacing-2);
    }
    .features ::slotted(*) {
      color: var(--crusher-fg);
      font-size: var(--crusher-font-size-sm);
      line-height: var(--crusher-line-height-snug);
    }

    .cta {
      display: flex;
      flex-direction: column;
      gap: var(--crusher-spacing-2);
    }
    .cta ::slotted(*) {
      width: 100%;
    }
  `;

  render() {
    const ariaLabel = this.planName ? `${this.planName} plan` : 'Plan';
    return html`
      <header class="header" role="group" aria-label=${ariaLabel}>
        <div class="badges">
          ${this.current ? html`<span class="badge-builtin"><span class="dot" aria-hidden="true"></span>Current plan</span>` : null}
          <slot name="badge"></slot>
        </div>
        <h3 class="plan-name">${this.planName}</h3>
        <div class="price">${this.price}</div>
        ${this.description ? html`<p class="description">${this.description}</p>` : null}
      </header>
      <div class="features"><slot></slot></div>
      <div class="cta"><slot name="cta"></slot></div>
    `;
  }
}

customElements.define('crusher-plan-card', CrusherPlanCard);
